import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import {
  canManageCalendarConnections,
  canViewCalendarConnections,
} from './platformAccess';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function normalizeProvider(value = '') {
  const provider = normalizeString(value).toLowerCase();
  if (['google', 'outlook'].includes(provider)) return provider;
  return provider || 'google';
}

function normalizeOwnerType(value = '') {
  const ownerType = normalizeString(value).toLowerCase();
  if (['organization', 'instructor', 'user'].includes(ownerType)) return ownerType;
  return 'user';
}

function normalizeStatus(value = '') {
  const status = normalizeString(value).toLowerCase();
  if (['connected', 'error', 'disabled', 'expired'].includes(status)) return status;
  return 'connected';
}

function normalizeScopes(scopes = []) {
  if (Array.isArray(scopes)) {
    return scopes.map((scope) => normalizeString(scope)).filter(Boolean);
  }
  return normalizeString(scopes)
    .split(',')
    .map((scope) => scope.trim())
    .filter(Boolean);
}

function buildConnectionLabel(provider = '') {
  return provider === 'outlook' ? 'Outlook Calendar' : 'Google Calendar';
}

function buildConnectionId(provider = 'calendar') {
  return `${provider}_connection_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function normalizeCalendarConnection(record = {}, fallbackId = '') {
  const id = record.id || record.calendarConnectionId || fallbackId || '';
  const provider = normalizeProvider(record.provider || 'google');
  const status = normalizeStatus(record.status || (record.syncEnabled === false ? 'disabled' : 'connected'));
  const syncEnabled = record.syncEnabled !== false && status !== 'disabled' && status !== 'expired';

  return {
    id,
    calendarConnectionId: id,
    ownerType: normalizeOwnerType(record.ownerType || 'user'),
    ownerId: normalizeString(record.ownerId || ''),
    provider,
    status,
    displayName: normalizeString(record.displayName || buildConnectionLabel(provider)),
    email: normalizeString(record.email || ''),
    externalCalendarRef: normalizeString(record.externalCalendarRef || ''),
    externalCalendarName: normalizeString(record.externalCalendarName || ''),
    scopesGranted: normalizeScopes(record.scopesGranted || []),
    connectedAt: record.connectedAt || null,
    updatedAt: record.updatedAt || null,
    lastSyncedAt: record.lastSyncedAt || null,
    lastError: normalizeString(record.lastError || ''),
    syncEnabled,
    metadata: record.metadata || {},
    isDefault: Boolean(record.isDefault),
  };
}

function toConnectionPayload(connection = {}) {
  const normalized = normalizeCalendarConnection(connection);
  return {
    ...normalized,
    syncEnabled: normalized.syncEnabled,
  };
}

export async function createCalendarConnection(
  db,
  {
    ownerType = 'user',
    ownerId = '',
    organizationId = '',
    currentUserId = '',
    userData = {},
    connection = {},
  } = {}
) {
  const normalizedOwnerType = normalizeOwnerType(ownerType || connection.ownerType);
  const normalizedOwnerId = normalizeString(ownerId || connection.ownerId || '');

  if (!canManageCalendarConnections(userData, {
    currentUserId,
    ownerType: normalizedOwnerType,
    ownerId: normalizedOwnerId,
    organizationId,
  })) {
    throw new Error('Not authorized to manage calendar connections.');
  }

  const normalized = normalizeCalendarConnection({
    ...connection,
    ownerType: normalizedOwnerType,
    ownerId: normalizedOwnerId,
  });

  const payload = {
    ...toConnectionPayload(normalized),
    connectedAt: normalized.connectedAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.CALENDAR_CONNECTIONS), payload);
  const connectionId = docRef?.id || normalized.calendarConnectionId || buildConnectionId(normalized.provider);
  return {
    ...normalized,
    id: connectionId,
    calendarConnectionId: connectionId,
  };
}

export async function getCalendarConnectionsForOwner(
  db,
  {
    ownerType = '',
    ownerId = '',
    currentUserId = '',
    userData = {},
  } = {}
) {
  const normalizedOwnerType = normalizeOwnerType(ownerType);
  const normalizedOwnerId = normalizeString(ownerId);

  if (!normalizedOwnerId) return [];
  if (!canViewCalendarConnections(userData, {
    currentUserId,
    ownerType: normalizedOwnerType,
    ownerId: normalizedOwnerId,
  })) {
    return [];
  }

  const snapshot = await getDocs(query(
    collection(db, PLATFORM_COLLECTIONS.CALENDAR_CONNECTIONS),
    where('ownerType', '==', normalizedOwnerType),
    where('ownerId', '==', normalizedOwnerId)
  ));

  return snapshot.docs
    .map((docSnap) => normalizeCalendarConnection({ id: docSnap.id, ...docSnap.data() }))
    .sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      if (a.syncEnabled !== b.syncEnabled) return a.syncEnabled ? -1 : 1;
      return a.provider.localeCompare(b.provider);
    });
}

export async function updateCalendarConnection(
  db,
  {
    calendarConnectionId = '',
    currentUserId = '',
    userData = {},
    organizationId = '',
    connection = {},
  } = {}
) {
  if (!calendarConnectionId) {
    throw new Error('calendarConnectionId is required.');
  }

  const snapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.CALENDAR_CONNECTIONS, calendarConnectionId));
  if (!snapshot || typeof snapshot.exists !== 'function') {
    const normalized = normalizeCalendarConnection({
      ...connection,
      id: calendarConnectionId,
      calendarConnectionId,
    });
    await setDoc(
      doc(db, PLATFORM_COLLECTIONS.CALENDAR_CONNECTIONS, calendarConnectionId),
      {
        ...toConnectionPayload(normalized),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return normalized;
  }

  if (!snapshot.exists()) {
    throw new Error('Calendar connection not found.');
  }

  const existing = normalizeCalendarConnection({ id: snapshot.id, ...snapshot.data() });
  if (!canManageCalendarConnections(userData, {
    currentUserId,
    ownerType: existing.ownerType,
    ownerId: existing.ownerId,
    organizationId,
  })) {
    throw new Error('Not authorized to manage calendar connections.');
  }

  const normalized = normalizeCalendarConnection({
    ...existing,
    ...connection,
    ownerType: existing.ownerType,
    ownerId: existing.ownerId,
  });

  await setDoc(
    doc(db, PLATFORM_COLLECTIONS.CALENDAR_CONNECTIONS, calendarConnectionId),
    {
      ...toConnectionPayload(normalized),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return normalized;
}

export async function disableCalendarConnection(
  db,
  {
    calendarConnectionId = '',
    currentUserId = '',
    userData = {},
    organizationId = '',
  } = {}
) {
  return updateCalendarConnection(db, {
    calendarConnectionId,
    currentUserId,
    userData,
    organizationId,
    connection: {
      status: 'disabled',
      syncEnabled: false,
    },
  });
}

export async function markCalendarConnectionError(
  db,
  {
    calendarConnectionId = '',
    lastError = '',
  } = {}
) {
  if (!calendarConnectionId) throw new Error('calendarConnectionId is required.');

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.CALENDAR_CONNECTIONS, calendarConnectionId), {
    status: 'error',
    syncEnabled: false,
    lastError: normalizeString(lastError || 'Calendar connection error'),
    updatedAt: serverTimestamp(),
  });

  return {
    calendarConnectionId,
    status: 'error',
    lastError: normalizeString(lastError || 'Calendar connection error'),
  };
}

export async function markCalendarConnectionConnected(
  db,
  {
    calendarConnectionId = '',
    metadata = null,
  } = {}
) {
  if (!calendarConnectionId) throw new Error('calendarConnectionId is required.');

  const payload = {
    status: 'connected',
    syncEnabled: true,
    lastError: '',
    lastSyncedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (metadata && typeof metadata === 'object') {
    payload.metadata = metadata;
  }

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.CALENDAR_CONNECTIONS, calendarConnectionId), payload);

  return {
    calendarConnectionId,
    status: 'connected',
  };
}

export function getDefaultCalendarConnectionForOwner(
  connections = [],
  { provider = '' } = {}
) {
  const normalizedProvider = normalizeProvider(provider || '');
  const normalizedConnections = (connections || [])
    .map((connection) => normalizeCalendarConnection(connection))
    .filter((connection) => connection.syncEnabled && connection.status === 'connected');

  if (normalizedProvider) {
    const matchingConnection = normalizedConnections.find((connection) => connection.provider === normalizedProvider);
    if (matchingConnection) return matchingConnection;
  }

  return normalizedConnections.find((connection) => connection.isDefault) || normalizedConnections[0] || null;
}
